import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Save, Loader2, Image, Palette, Globe, Mail, FileText } from 'lucide-react';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    app_name: 'Pathways',
    app_logo_url: '',
    primary_color: '#CCFF00',
    welcome_message: '',
    terms_url: '',
    privacy_url: '',
    contact_email: ''
  });

  const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
  const isSuperAdmin = adminUser.admin_role === 'super_admin';

  useEffect(() => {
    if (!isSuperAdmin) {
      toast.error('Only Super Admin can access settings');
      window.location.href = '/admin/dashboard';
      return;
    }
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);

    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(`${API}/admin/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (!isSuperAdmin) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-12 h-12 text-ssdp-teal animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            App Settings
          </h1>
          <p className="text-gray-600">
            Configure Pathways app appearance and information
          </p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-gradient-to-r from-ssdp-teal to-ssdp-purple text-white"
        >
          <Save className="w-5 h-5 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <div className="space-y-6">
        {/* Branding */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-6">
            <Image className="w-5 h-5 text-ssdp-teal" />
            <h2 className="text-xl font-bold text-gray-900">Branding</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                App Name
              </label>
              <input
                type="text"
                value={settings.app_name}
                onChange={(e) => setSettings({ ...settings, app_name: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-ssdp-teal focus:outline-none"
                placeholder="Pathways"
              />
              <p className="text-xs text-gray-500 mt-1">
                Displayed in the app header and branding
              </p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Logo URL
              </label>
              <input
                type="url"
                value={settings.app_logo_url}
                onChange={(e) => setSettings({ ...settings, app_logo_url: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-ssdp-teal focus:outline-none"
                placeholder="https://..."
              />
              <p className="text-xs text-gray-500 mt-1">
                Optional: Custom logo image URL
              </p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Primary Color
              </label>
              <div className="flex gap-3 items-center">
                <input
                  type="color"
                  value={settings.primary_color}
                  onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                  className="w-20 h-12 border-2 border-gray-200 rounded-lg cursor-pointer"
                />
                <input
                  type="text"
                  value={settings.primary_color}
                  onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                  className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-ssdp-teal focus:outline-none font-mono"
                  placeholder="#CCFF00"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Main accent color used throughout the app
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-6">
            <FileText className="w-5 h-5 text-purple-600" />
            <h2 className="text-xl font-bold text-gray-900">Content</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Welcome Message
              </label>
              <textarea
                value={settings.welcome_message}
                onChange={(e) => setSettings({ ...settings, welcome_message: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-ssdp-teal focus:outline-none"
                rows="3"
                placeholder="Navigate life's complex decisions with confidence..."
              />
              <p className="text-xs text-gray-500 mt-1">
                Shown on the homepage and onboarding
              </p>
            </div>
          </div>
        </div>

        {/* Links */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-6">
            <Globe className="w-5 h-5 text-green-600" />
            <h2 className="text-xl font-bold text-gray-900">Legal & Links</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Terms of Use URL
              </label>
              <input
                type="url"
                value={settings.terms_url}
                onChange={(e) => setSettings({ ...settings, terms_url: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-ssdp-teal focus:outline-none"
                placeholder="https://..."
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Privacy Policy URL
              </label>
              <input
                type="url"
                value={settings.privacy_url}
                onChange={(e) => setSettings({ ...settings, privacy_url: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-ssdp-teal focus:outline-none"
                placeholder="https://..."
              />
            </div>
          </div>
        </div>

        {/* Contact */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-6">
            <Mail className="w-5 h-5 text-amber-600" />
            <h2 className="text-xl font-bold text-gray-900">Contact</h2>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Support Email
            </label>
            <input
              type="email"
              value={settings.contact_email}
              onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-ssdp-teal focus:outline-none"
              placeholder="support@ssdp.org"
            />
            <p className="text-xs text-gray-500 mt-1">
              Contact email for user support inquiries
            </p>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end pt-4">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-gradient-to-r from-ssdp-teal to-ssdp-purple text-white px-8 py-3"
          >
            <Save className="w-5 h-5 mr-2" />
            {saving ? 'Saving Changes...' : 'Save All Settings'}
          </Button>
        </div>

        {/* Info */}
        <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-6">
          <p className="text-sm text-blue-900">
            <strong>Note:</strong> Changes to app settings will take effect immediately for all users. 
            Make sure to test changes before saving.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
